import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Background from '../Background/Background';
import ProviderMark from '../common/ProviderMark/ProviderMark';
import { log, error as logError, warn } from '../../utils/logger';
import { haptic } from '../../utils/haptics';
import { mapAuthError, mapProviderHint } from '../../services/authErrorMap';
import {
    fetchProviderHint,
    validatePasswordLocally,
    PASSWORD_MIN_LENGTH,
} from '../../services/emailPasswordAuth';
import rhemnLogo from '../../assets/rhemn-logo.png';
import './SignInScreen.css';

interface SignInScreenProps {
    /** Purchase auth gate (Fix B, native): one quiet context line under the
     *  sign-in options — "Sign in first — your Pro goes with you everywhere."
     *  Absent on the normal cold-start screen. */
    contextLine?: string;
    /** Invoked after a successful sign-in (gate overlay closes itself). */
    onSignedIn?: () => void;
    /** When provided, renders a ✕ — dismissing returns to the originating
     *  surface unchanged, no nag. Cold-start never passes it. */
    onDismiss?: () => void;
}

type Step = 'choose' | 'password' | 'sent' | 'verify';
type Mode = 'signin' | 'create';

/** Must stay in sync with --dur-fast, which drives signin-step-out. */
const EXIT_MS = 200;

/** Shallow — real validation is Firebase's job. This only gates the button's warm-up. */
const LOOKS_LIKE_EMAIL = /.+@.+\..+/;

/**
 * Must not be SHORTER than the backend's own per-address throttle
 * (COOLDOWN_MS in backend-graphql/src/services/authEmailService.js, currently
 * 60s). The backend deliberately reports success even when it throttles — that
 * is what stops the endpoint being an account-existence oracle — so a shorter
 * client cooldown would let the user tap Resend, see "Sent again", and receive
 * nothing. The countdown is a promise about what actually happens.
 */
const RESEND_COOLDOWN_S = 60;

/**
 * After this many resends the backend escalates to a 15-minute window
 * (BURST_LIMIT / BURST_COOLDOWN_MS in the same file). Mirrored here for the
 * same reason.
 */
const RESEND_BURST_LIMIT = 3;
const RESEND_BURST_COOLDOWN_S = 15 * 60;

const cooldownFor = (sentCount: number): number =>
    (sentCount >= RESEND_BURST_LIMIT ? RESEND_BURST_COOLDOWN_S : RESEND_COOLDOWN_S);

/**
 * The link that opened this step already counted against the backend's budget —
 * its throttle row starts at sentCount 1. Starting the client at 0 would let the
 * UI still be offering a 60-second cooldown while the backend had already
 * escalated to fifteen minutes, so a resend would be silently swallowed while
 * the UI announced it was sent.
 */
const INITIAL_SENT_COUNT = 1;

/** While the verify step is mounted, poll fast at first, then back off. */
const POLL_FAST_MS = 3000;
const POLL_SLOW_MS = 10000;
const POLL_FAST_WINDOW_MS = 30000;
const POLL_MAX_MS = 10 * 60 * 1000;

/**
 * Strength is a count, not a hue — see the meter's CSS. Length dominates:
 * composition rules push users toward "Password1!" and away from passphrases
 * (NIST SP 800-63B), so they only ever promote, never gate.
 */
const scorePassword = (password: string): number => {
    if (password.length < PASSWORD_MIN_LENGTH) return 0;
    const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(password)).length;
    if (password.length >= 20 || (password.length >= 16 && classes >= 3)) return 3;
    if (password.length >= 16 || classes >= 3) return 2;
    return 1;
};

const SignInScreen: React.FC<SignInScreenProps> = ({ contextLine, onSignedIn, onDismiss }) => {
    const { t } = useTranslation(['auth', 'common']);
    const {
        signInGoogle, signInApple,
        signUpEmail, signInEmail, sendPasswordReset, resendVerification, checkVerification,
        pendingVerification, clearPendingVerification, clearAuthError,
        loading, error, errorCode,
    } = useAuth();

    const [isSigningIn, setIsSigningIn] = useState(false);
    const [step, setStep] = useState<Step>('choose');
    const [leavingStep, setLeavingStep] = useState<Step | null>(null);
    const [mode, setMode] = useState<Mode>('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [identityHint, setIdentityHint] = useState<{ key: string; error: boolean } | null>(null);
    const [passwordHint, setPasswordHint] = useState<{ key: string; error: boolean } | null>(null);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [cooldown, setCooldown] = useState(0);
    const [resendCount, setResendCount] = useState(INITIAL_SENT_COUNT);
    const [a11yStatus, setA11yStatus] = useState('');

    const autoStartedRef = useRef(false);
    const formRef = useRef<HTMLFormElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const sentTitleRef = useRef<HTMLHeadingElement>(null);
    const verifyTitleRef = useRef<HTMLHeadingElement>(null);

    const busy = loading || isSigningIn || busyKey !== null;
    const emailLooksValid = LOOKS_LIKE_EMAIL.test(email.trim());

    // ── Step transitions ────────────────────────────────────────────────────
    const stepRef = useRef<Step>('choose');
    const exitTimerRef = useRef<number | undefined>(undefined);

    const goToStep = useCallback((next: Step) => {
        const current = stepRef.current;
        if (current === next) return;

        // Scheduling the exit timer here rather than inside a setStep updater:
        // updaters must be pure, and React invokes them twice under StrictMode —
        // which would leak a second timer and drop --leaving early.
        stepRef.current = next;
        setStep(next);
        setLeavingStep(current);

        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = window.setTimeout(() => setLeavingStep(null), EXIT_MS);

        haptic('light');
    }, []);

    useEffect(() => () => window.clearTimeout(exitTimerRef.current), []);

    // A pending verification can arrive from the boot-effect peek (an account that
    // signed up, closed the tab, and came back) — not just from this session.
    useEffect(() => {
        if (pendingVerification) {
            setEmail((current) => current || pendingVerification.email);
            goToStep('verify');
        }
    }, [pendingVerification, goToStep]);

    // ── OAuth ───────────────────────────────────────────────────────────────
    const runOAuth = useCallback(async (
        provider: 'google' | 'apple',
        run: () => Promise<{ user: unknown | null; error?: string }>,
    ) => {
        log(`[SignInScreen] Starting ${provider} sign-in...`);
        setIsSigningIn(true);
        setIdentityHint(null);
        try {
            const result = await run();
            if (result.error) {
                logError(`[SignInScreen] ${provider} sign-in error:`, result.error);
                haptic('warning');
            } else if (result.user) {
                onSignedIn?.();
            } else {
                warn(`[SignInScreen] ${provider} sign-in completed but no user returned`);
            }
        } catch (err) {
            logError(`[SignInScreen] ${provider} sign-in exception:`, err);
            haptic('warning');
        } finally {
            setIsSigningIn(false);
        }
    }, [onSignedIn]);

    const handleGoogleSignIn = useCallback(() => runOAuth('google', signInGoogle), [runOAuth, signInGoogle]);
    const handleAppleSignIn = useCallback(() => runOAuth('apple', signInApple), [runOAuth, signInApple]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const extAction = params.get('extAction');
        const provider = params.get('provider');

        if (autoStartedRef.current) return;
        if (extAction !== 'signin') return;
        if (isSigningIn || loading) return;

        autoStartedRef.current = true;

        if (provider === 'apple') {
            void handleAppleSignIn();
        } else {
            void handleGoogleSignIn();
        }
    }, [handleAppleSignIn, handleGoogleSignIn, isSigningIn, loading]);

    /**
     * An SPA never navigates, so Chrome's "form submitted, fields gone" heuristic
     * may never fire and the password is never offered for saving. Without this a
     * meaningful share of users create an account and can never get back in.
     */
    const offerToSaveCredentials = useCallback(() => {
        try {
            const w = window as any;
            if (!('credentials' in navigator) || !('PasswordCredential' in w) || !formRef.current) return;
            void navigator.credentials.store(new w.PasswordCredential(formRef.current)).catch(() => { });
        } catch {
            /* Safari doesn't implement this and falls back to its own heuristics. */
        }
    }, []);

    // ── Email flow ──────────────────────────────────────────────────────────
    const handleContinue = useCallback(async () => {
        const trimmed = email.trim();
        if (!LOOKS_LIKE_EMAIL.test(trimmed)) {
            setIdentityHint({ key: 'auth:email.errors.invalidEmail', error: true });
            emailRef.current?.focus();
            return;
        }

        setIdentityHint(null);
        setPasswordHint(null);
        setBusyKey('checking');
        goToStep('password');

        // Runs in parallel with the transition. Fail-open in every direction: the
        // hint is never load-bearing, and the try/catch also guarantees busyKey is
        // always released — otherwise a throw here would leave the primary button
        // stuck in its busy state with no way out.
        let hint: string | null = null;
        try {
            hint = mapProviderHint(await fetchProviderHint(trimmed));
        } catch {
            /* proceed to the password step */
        } finally {
            setBusyKey(null);
        }

        if (hint) {
            setIdentityHint({ key: hint, error: true });
            goToStep('choose');
        }
    }, [email, goToStep]);

    const handlePasswordSubmit = useCallback(async () => {
        if (mode === 'create') {
            const problem = validatePasswordLocally(password);
            if (problem) {
                setPasswordHint({
                    key: problem === 'tooShort'
                        ? 'auth:email.errors.passwordTooShort'
                        : 'auth:email.errors.passwordCommon',
                    error: true,
                });
                return;
            }
        } else if (!password) {
            setPasswordHint({ key: 'auth:email.errors.missingPassword', error: true });
            return;
        }

        setPasswordHint(null);
        setBusyKey(mode === 'create' ? 'creating' : 'signingIn');

        const result = mode === 'create'
            ? await signUpEmail(email.trim(), password)
            : await signInEmail(email.trim(), password);

        setBusyKey(null);

        if (result.user) {
            offerToSaveCredentials();
            onSignedIn?.();
            return;
        }

        if (result.pendingVerification) {
            offerToSaveCredentials();
            setResendCount(INITIAL_SENT_COUNT);
            setCooldown(cooldownFor(INITIAL_SENT_COUNT));
            goToStep('verify');
            return;
        }

        haptic('warning');
        setPasswordHint({ key: mapAuthError(result.errorCode), error: true });
        // Preserve and select rather than clear — the respectful "try again".
        passwordRef.current?.select();
    }, [mode, password, email, signUpEmail, signInEmail, onSignedIn, goToStep, offerToSaveCredentials]);

    const handleForgot = useCallback(async () => {
        // Narrow on purpose: only a send in flight blocks. The provider-hint check
        // ('checking') can still be running when the password step opens, and must not
        // swallow a Forgot-password tap.
        if (busyKey === 'sending') return;
        setBusyKey('sending');
        const result = await sendPasswordReset(email.trim());
        setBusyKey(null);
        // Always advance, whatever the SDK said: reporting "no such user" here is
        // the same enumeration oracle the explicit-mode toggle exists to avoid.
        if (result.ok || result.errorCode !== 'auth/network-request-failed') {
            setResendCount(INITIAL_SENT_COUNT);
            setCooldown(cooldownFor(INITIAL_SENT_COUNT));
            goToStep('sent');
        } else {
            setPasswordHint({ key: mapAuthError(result.errorCode), error: true });
        }
    }, [busyKey, email, sendPasswordReset, goToStep]);

    const handleResend = useCallback(async () => {
        if (cooldown > 0 || busyKey === 'sending') return;
        setBusyKey('sending');
        const isReset = step === 'sent';
        const result = isReset ? await sendPasswordReset(email.trim()) : await resendVerification();
        setBusyKey(null);
        if (result.ok) {
            setResendCount((c) => {
                const next = c + 1;
                setCooldown(cooldownFor(next));
                return next;
            });
            setA11yStatus(t(isReset ? 'auth:email.sent.resent' : 'auth:email.verify.resent'));
        }
    }, [cooldown, busyKey, step, email, sendPasswordReset, resendVerification, t]);

    const handleSubmit = useCallback((e: React.FormEvent) => {
        // CSP sets form-action 'none' (manifest.json), so the native submit is
        // blocked outright — this preventDefault is what keeps the form usable.
        e.preventDefault();
        if (busy) return;
        if (step === 'choose') void handleContinue();
        else if (step === 'password') void handlePasswordSubmit();
        else if (step === 'sent') { setPassword(''); goToStep('password'); }
        else if (step === 'verify') void checkVerification();
    }, [busy, step, handleContinue, handlePasswordSubmit, goToStep, checkVerification]);

    // ── Verification polling ────────────────────────────────────────────────
    useEffect(() => {
        if (step !== 'verify') return;

        const startedAt = Date.now();
        let timer: number;
        // `poll` awaits a network call, so cleanup can run while it is in flight.
        // Without this flag the resumed poll would schedule a NEW timeout that the
        // already-executed cleanup can never clear — leaving "Change email" with a
        // background poller that keeps running, and can still promote the user into
        // the very account they just backed out of.
        let cancelled = false;

        const poll = async () => {
            const elapsed = Date.now() - startedAt;
            if (cancelled || elapsed > POLL_MAX_MS) return;
            const { verified } = await checkVerification();
            if (cancelled || verified) return;
            timer = window.setTimeout(poll, elapsed < POLL_FAST_WINDOW_MS ? POLL_FAST_MS : POLL_SLOW_MS);
        };

        timer = window.setTimeout(poll, POLL_FAST_MS);

        // The link almost always opens in another tab; coming back here is the
        // strongest possible signal that it's worth re-checking immediately.
        const onVisible = () => { if (document.visibilityState === 'visible') void checkVerification(); };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [step, checkVerification]);

    // ── Cooldown tick ───────────────────────────────────────────────────────
    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [cooldown]);

    useEffect(() => {
        // Only the transition back to "Resend link" is announced — a per-second
        // live region would be torture.
        if (cooldown === 0 && resendCount > 0) setA11yStatus(t('auth:email.a11y.resendReady'));
    }, [cooldown, resendCount, t]);

    // ── Focus + announcements ───────────────────────────────────────────────
    useEffect(() => {
        if (step === 'password') {
            passwordRef.current?.focus();
            setA11yStatus(t(mode === 'create'
                ? 'auth:email.a11y.stepPasswordCreate'
                : 'auth:email.a11y.stepPasswordSignIn'));
        } else if (step === 'choose') {
            setA11yStatus(t('auth:email.a11y.stepEmail'));
        } else if (step === 'sent') {
            sentTitleRef.current?.focus();
            setA11yStatus(t('auth:email.a11y.stepSent'));
        } else if (step === 'verify') {
            verifyTitleRef.current?.focus();
            setA11yStatus(t('auth:email.a11y.stepVerify'));
        }
        // `mode` intentionally excluded: switching mode mid-step must not re-steal focus.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, t]);

    // ── Escape ──────────────────────────────────────────────────────────────
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (step === 'password') { setPassword(''); goToStep('choose'); }
            else if (step === 'sent') { setPassword(''); goToStep('password'); }
            // 'verify' has no way back — the account exists and is unusable until
            // verified. 'choose' only dismisses when there is a surface to return
            // to; cold start must never be escapable.
            else if (step === 'choose' && onDismiss) onDismiss();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [step, goToStep, onDismiss]);

    const handleChangeEmail = useCallback(() => {
        setPassword('');
        setPasswordHint(null);
        // Also the only escape from the verify step. Without this a typo'd address
        // is an unrecoverable dead end: the account exists, the link went to an
        // inbox the user cannot read, and the pending-verification effect would
        // bounce them straight back here.
        clearPendingVerification();
        clearAuthError();
        goToStep('choose');
        window.setTimeout(() => {
            emailRef.current?.focus();
            emailRef.current?.select();
        }, 0);
    }, [goToStep, clearPendingVerification, clearAuthError]);

    const switchMode = useCallback(() => {
        setMode((m) => (m === 'signin' ? 'create' : 'signin'));
        setPasswordHint(null);
        setPassword('');
    }, []);

    // OAuth-level failures share the identity hint line so there is exactly one
    // error channel and the card never grows.
    const oauthHint = useMemo(() => {
        if (!error) return null;
        return { key: mapAuthError(errorCode), error: true };
    }, [error, errorCode]);

    const activeIdentityHint = identityHint || (step === 'choose' ? oauthHint : null);
    const strength = scorePassword(password);
    const settled = step !== 'choose';
    const cooldownLabel = `${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, '0')}`;

    const stepClass = (s: Step) =>
        `signin-step signin-step--${s}${step === s ? ' signin-step--active' : ''}${leavingStep === s ? ' signin-step--leaving' : ''}`;

    const renderHint = (
        id: string,
        hint: { key: string; error: boolean } | null,
        extraClass = '',
    ) => (
        <div
            id={id}
            className={`signin-hint ${extraClass}${hint?.error ? ' signin-hint--error' : ''}`}
            role="status"
            aria-live="polite"
        >
            {hint && (
                // key= remounts the node so the crossfade replays on every new
                // message (same technique as fi-placeholder-in).
                <span className="signin-hint__text" key={hint.key}>
                    {hint.key === 'auth:email.errors.emailInUse' ? (
                        <Trans
                            i18nKey="auth:email.errors.emailInUse"
                            components={{
                                signInBtn: (
                                    <button type="button" key="signInInstead" className="signin-hint__inline-btn" onClick={switchMode}>
                                        Sign in instead
                                    </button>
                                ),
                            }}
                        />
                    ) : t(hint.key)}
                </span>
            )}
        </div>
    );

    return (
        <div className="signin-screen-wrapper">
            <Background />
            <div className="signin-overlay" />

            {onDismiss && (
                <button
                    type="button"
                    className="signin-dismiss tap-target"
                    onClick={onDismiss}
                    aria-label={t('common:actions.close')}
                >
                    ×
                </button>
            )}

            <div className="signin-content-centered">
                <div className="signin-card">

                    <img src={rhemnLogo} alt={t('auth:signIn.logo_alt')} className="signin-logo" />
                    <p className="signin-tagline">{t('auth:signIn.tagline')}</p>

                    {/* ONE form wraps the identity field AND the stage. Both inputs must
                        always be in the same form element or password managers never
                        offer to fill or save. */}
                    <form ref={formRef} className="signin-form" onSubmit={handleSubmit} noValidate>

                        {/* Persists across every step; morphs into a settled chip. */}
                        <label className="signin-sr-only" htmlFor="signin-email">
                            {t('auth:email.identity.label')}
                        </label>
                        <div className={`signin-identity${settled ? ' signin-identity--settled' : ''}`}>
                            <Mail size={18} strokeWidth={1.75} className="signin-identity__icon" aria-hidden="true" />
                            <input
                                ref={emailRef}
                                id="signin-email"
                                className="signin-identity__input"
                                type="email"
                                name="username"
                                /* "username", NOT "email" — `email` is the address-book token;
                                   `username` is what managers key a login on. Getting this
                                   wrong is the #1 cause of "sees the email, never offers the
                                   password". */
                                autoComplete="username"
                                inputMode="email"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                enterKeyHint="next"
                                placeholder={t('auth:email.identity.placeholder')}
                                value={email}
                                readOnly={settled}
                                aria-describedby="signin-hint-identity"
                                aria-invalid={activeIdentityHint?.error || undefined}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setIdentityHint(null);
                                    // Also drop any error left over from a previous
                                    // attempt — it lives in AuthContext and would
                                    // otherwise sit under the field while the user
                                    // types a fresh address.
                                    clearAuthError();
                                }}
                            />
                            <button
                                type="button"
                                className="signin-identity__change tap-target"
                                onClick={handleChangeEmail}
                                tabIndex={settled ? 0 : -1}
                                aria-hidden={!settled}
                                aria-label={t('auth:email.identity.change_aria')}
                            >
                                {t('auth:email.identity.change')}
                            </button>
                        </div>

                        {renderHint('signin-hint-identity', activeIdentityHint, 'signin-hint--identity')}

                        <div className="signin-stage">

                            {/* ── choose ──────────────────────────────────────────── */}
                            <section className={stepClass('choose')} inert={step !== 'choose'}>
                                <button
                                    type="submit"
                                    className={`signin-btn signin-btn--primary${emailLooksValid ? ' is-ready' : ''}${busyKey === 'checking' ? ' is-busy' : ''}`}
                                    aria-busy={busyKey === 'checking' || undefined}
                                >
                                    <span>{busyKey === 'checking' ? t('auth:email.busy.checking') : t('auth:email.actions.continue')}</span>
                                </button>

                                <div className="signin-divider">{t('auth:signIn.divider')}</div>

                                <div className="signin-buttons">
                                    {/* Apple first per Apple HIG — a native email field is not a
                                        third-party sign-in option, so the requirement is met. */}
                                    <button
                                        type="button"
                                        className="signin-btn signin-btn--apple"
                                        onClick={handleAppleSignIn}
                                        disabled={busy}
                                        aria-label={t('auth:signIn.continueApple_aria')}
                                    >
                                        <svg className="signin-btn-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                                        </svg>
                                        <span>{busy ? t('auth:signIn.signingIn') : t('auth:signIn.continueApple')}</span>
                                    </button>

                                    <button
                                        type="button"
                                        className="signin-btn signin-btn--google"
                                        onClick={handleGoogleSignIn}
                                        disabled={busy}
                                        aria-label={t('auth:signIn.continueGoogle_aria')}
                                    >
                                        <ProviderMark provider="google" size={20} className="signin-btn-icon" />
                                        <span>{busy ? t('auth:signIn.signingIn') : t('auth:signIn.continueGoogle')}</span>
                                    </button>
                                </div>
                            </section>

                            {/* ── password ────────────────────────────────────────── */}
                            <section className={stepClass('password')} inert={step !== 'password'}>
                                <label className="signin-sr-only" htmlFor="signin-password">
                                    {t(mode === 'create' ? 'auth:email.password.labelNew' : 'auth:email.password.label')}
                                </label>
                                <div className={`signin-password${passwordHint?.error ? ' is-invalid' : ''}`}>
                                    <Lock size={18} strokeWidth={1.75} className="signin-password__icon" aria-hidden="true" />
                                    <input
                                        ref={passwordRef}
                                        id="signin-password"
                                        className="signin-password__input"
                                        /* key= swaps the DOM node when mode flips. Mutating
                                           autoComplete on a live node leaves managers on their
                                           cached, stale decision. */
                                        key={mode === 'create' ? 'new' : 'current'}
                                        type={showPassword ? 'text' : 'password'}
                                        name={mode === 'create' ? 'new-password' : 'password'}
                                        autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
                                        enterKeyHint="go"
                                        placeholder={t(mode === 'create' ? 'auth:email.password.placeholderNew' : 'auth:email.password.placeholder')}
                                        value={password}
                                        aria-describedby="signin-hint-password"
                                        aria-invalid={passwordHint?.error || undefined}
                                        onChange={(e) => { setPassword(e.target.value); setPasswordHint(null); }}
                                    />
                                    <button
                                        type="button"
                                        className="signin-password__reveal tap-target"
                                        onClick={() => setShowPassword((v) => !v)}
                                        aria-pressed={showPassword}
                                        aria-label={t(showPassword ? 'auth:email.password.hide' : 'auth:email.password.show')}
                                    >
                                        {showPassword
                                            ? <EyeOff size={18} strokeWidth={1.75} aria-hidden="true" />
                                            : <Eye size={18} strokeWidth={1.75} aria-hidden="true" />}
                                    </button>
                                </div>

                                {mode === 'create' && (
                                    // aria-hidden: the hint line is the accessible carrier.
                                    <div className="signin-strength" data-level={strength} aria-hidden="true">
                                        <i className="signin-strength__seg" />
                                        <i className="signin-strength__seg" />
                                        <i className="signin-strength__seg" />
                                    </div>
                                )}

                                {renderHint(
                                    'signin-hint-password',
                                    passwordHint || (mode === 'create' && password
                                        ? { key: `auth:email.strength.${strength === 0 ? 'tooShort' : `level${strength}`}`, error: false }
                                        : null),
                                    'signin-hint--password',
                                )}

                                <button
                                    type="submit"
                                    className={`signin-btn signin-btn--primary is-ready${busyKey === 'creating' || busyKey === 'signingIn' ? ' is-busy' : ''}`}
                                    aria-busy={busyKey === 'creating' || busyKey === 'signingIn' || undefined}
                                >
                                    <span>
                                        {busyKey === 'creating' ? t('auth:email.busy.creating')
                                            : busyKey === 'signingIn' ? t('auth:email.busy.signingIn')
                                                : t(mode === 'create' ? 'auth:email.actions.createAccount' : 'auth:email.actions.signIn')}
                                    </span>
                                </button>

                                {mode === 'signin' && (
                                    // By step 2 we already know the email, so this needs no
                                    // further input — one tap sends the link.
                                    <button
                                        type="button"
                                        className="signin-link signin-link--forgot tap-target"
                                        onClick={handleForgot}
                                    >
                                        {t('auth:email.actions.forgot')}
                                    </button>
                                )}
                            </section>

                            {/* ── sent (password reset) ───────────────────────────── */}
                            <section className={stepClass('sent')} inert={step !== 'sent'}>
                                <h2 className="signin-step__title" ref={sentTitleRef} tabIndex={-1}>
                                    {t('auth:email.sent.title')}
                                </h2>
                                <p className="signin-step__body">
                                    {t('auth:email.sent.body', { email: email.trim() })}
                                </p>
                                <button type="submit" className="signin-btn signin-btn--primary is-ready">
                                    <span>{t('auth:email.actions.backToSignIn')}</span>
                                </button>
                                <button
                                    type="button"
                                    className="signin-link signin-link--resend tap-target"
                                    onClick={handleResend}
                                    /* aria-disabled, not disabled — it must stay focusable so a
                                       screen-reader user can hear the remaining time. */
                                    aria-disabled={cooldown > 0 || undefined}
                                >
                                    {cooldown > 0
                                        ? t('auth:email.actions.resendIn', { time: cooldownLabel })
                                        : t('auth:email.actions.resend')}
                                </button>
                            </section>

                            {/* ── verify (post-create) ────────────────────────────────
                                HARD GATE. The primary re-checks verification; it does NOT
                                call onSignedIn(). An unverified account's token is rejected
                                by the backend, so letting the user through would put the app
                                in a state where every request fails. */}
                            <section className={stepClass('verify')} inert={step !== 'verify'}>
                                <h2 className="signin-step__title" ref={verifyTitleRef} tabIndex={-1}>
                                    {t('auth:email.verify.title')}
                                </h2>
                                <p className="signin-step__body">
                                    {t('auth:email.verify.body', { email: email.trim() })}
                                </p>
                                <button type="submit" className="signin-btn signin-btn--primary is-ready">
                                    <span>{t('auth:email.actions.iVerified')}</span>
                                </button>
                                <button
                                    type="button"
                                    className="signin-link signin-link--resend tap-target"
                                    onClick={handleResend}
                                    aria-disabled={cooldown > 0 || undefined}
                                >
                                    {cooldown > 0
                                        ? t('auth:email.actions.resendIn', { time: cooldownLabel })
                                        : t('auth:email.actions.resend')}
                                </button>
                            </section>
                        </div>
                    </form>

                    {/* Outside the stage: mode is orthogonal to step, and must be visible
                        at step 1 so intent is set before typing. Hidden (not unmounted) on
                        sent/verify so the card height holds. */}
                    <p className={`signin-switch${step === 'sent' || step === 'verify' ? ' signin-switch--hidden' : ''}`}>
                        <Trans
                            i18nKey={mode === 'signin' ? 'auth:email.switch.toCreate' : 'auth:email.switch.toSignIn'}
                            components={{
                                switchBtn: (
                                    <button type="button" key="switch" className="signin-switch__btn tap-target" onClick={switchMode}>
                                        switch
                                    </button>
                                ),
                            }}
                        />
                    </p>

                    {contextLine && <p className="signin-context-line">{contextLine}</p>}

                    <p className="signin-footer">
                        <Trans
                            i18nKey="auth:signIn.footer"
                            components={{
                                privacyLink: (
                                    <a
                                        key="privacy"
                                        href="https://rhemn.com/privacy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="signin-footer-link"
                                    >
                                        Privacy Policy
                                    </a>
                                ),
                            }}
                        />
                    </p>

                    <p className="signin-a11y-status signin-sr-only" role="status" aria-live="polite">
                        {a11yStatus}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SignInScreen;
